import get from 'lodash/get';
import set from 'lodash/set';

/**
 * Stripped-down and type-annotated versions of Lodash functions
 */

export const findLast = <T>(collection: T[], predicate: (item: T) => boolean) => {
  for (let i = collection.length - 1; i >= 0; i -= 1) {
    if (predicate(collection[i])) {
      return collection[i];
    }
  }
};

export const findKey = <T extends object>(
  collection: T,
  predicate: (value: T[keyof T]) => boolean
): keyof T | undefined => {
  const matched = Object.entries(collection).filter(([, val]) => predicate(val));

  return get(matched, [0, 0]) as keyof T | undefined;
};

export const omitBy = <T extends object>(
  target: T,
  predicate: (value: T[keyof T]) => boolean
): Partial<T> => {
  const newObject = Object.entries(target)
    .filter(([, val]) => !predicate(val))
    .reduce(
      (accumulator, [key, value]) => ({
        ...accumulator,
        [key]: value,
      }),
      {}
    );

  return newObject;
};

export const uniqBy = <T, K extends keyof any>(target: T[], matchFunc: (value: T) => K): T[] => {
  const uniqSet = new Set<K>();

  return target.filter((item) => {
    const value = matchFunc(item);

    if (!uniqSet.has(value)) {
      uniqSet.add(value);
      return true;
    }
    return false;
  });
};

export const groupBy = <T, K extends keyof any>(target: T[], matchFunc: (value: T) => K) => {
  const groupedObject = {} as Record<K, T[]>;

  target.forEach((object) => {
    const key = matchFunc(object);
    if (!groupedObject[key]) {
      groupedObject[key] = [];
    }
    groupedObject[key].push(object);
  });

  return groupedObject;
};

// https://levelup.gitconnected.com/omit-is-being-removed-in-lodash-5-c1db1de61eaf
/**
 * Creates an object composed of the own and inherited enumerable property paths of object that are not omitted.
 * @param object - The source object.
 * @param keys - The property paths to omit.
 */

export const omit = <T extends Record<string, any>, K extends string>(
  object: T,
  keys: K[]
): Pick<T, Exclude<keyof T, K>> => {
  const clonedObject = { ...object };
  keys.forEach((path) => {
    if (path in clonedObject) {
      delete clonedObject[path];
    }
  });
  return clonedObject;
};

/**
 * Creates an object composed of the picked object properties.
 * @param object - The source object.
 * @param keys - The property paths to pick.
 */
export const pick = <T extends Record<string, any>, K extends string>(
  object: T,
  keys: readonly K[]
): {
  [P in K]: T[P];
} =>
  keys.reduce((obj, key) => {
    if (key in object) {
      obj[key] = object[key];
    } else if (key.includes('.')) {
      // handle nested path
      const nestedPath = key.split('.');
      const value = get(object, nestedPath);
      set(obj, nestedPath, value);
    }
    return obj;
  }, {} as T);

// https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_clamp
export const clamp = (number: number, boundOne: number, boundTwo?: number) => {
  if (!boundTwo) {
    return Math.max(number, boundOne) === boundOne ? number : boundOne;
  } else if (Math.min(number, boundOne) === number) {
    return boundOne;
  } else if (Math.max(number, boundTwo) === number) {
    return boundTwo;
  }
  return number;
};

/**
 * Creates an array of numbers (positive and/or negative) progressing from start up to, but not including, end.
 * A step of -1 is used if a negative start is specified without an end or step.
 * If end is not specified, it's set to start with start then set to 0.
 * ** NOTE: only partially implemented lodash/range (no steps, and can't take negative start/end)
 * @param start
 * @param end
 * @returns
 */
export const range = (start: number = 0, end?: number) => {
  let tempStart = start;
  const tempEnd = end ?? start;

  if (typeof end === 'undefined') {
    tempStart = 0;
  }
  return Array.from({ length: Math.abs(tempEnd - tempStart) }, (_, i) => i + tempStart);
};

export default range;

export const mapKeys = (
  obj: Record<string, any>,
  fn: (v: Record<string, any>, k: string, o: Record<string, any>) => string
) =>
  Object.keys(obj).reduce((acc, k: string) => {
    acc[fn(obj[k], k, obj)] = obj[k];
    return acc;
  }, {});
